import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError } from "../lib/api";
import type { User } from "../types";

export type SubscriptionInfo = {
  plan: "free" | "pro_talent" | "team_fit" | "scale_up";
  status: "active" | "past_due" | "canceled" | "trialing";
  current_period_end: string | null;
};

export type CreditsInfo = {
  interview_credits: number;
  report_credits: number;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  subscription: SubscriptionInfo | null;
  credits: CreditsInfo | null;
  loginWithGoogle: (idToken: string, role?: "talent" | "company") => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  setUser: (user: User | null) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [credits, setCredits] = useState<CreditsInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = useCallback(async () => {
    try {
      const data = await api.get<{ subscription: SubscriptionInfo; credits: CreditsInfo }>("/api/payments/subscription");
      setSubscription(data.subscription);
      setCredits(data.credits);
    } catch {
      setSubscription(null);
      setCredits(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ user: User }>("/api/auth/me");
      setUser(data.user);
      if (data.user) {
        await refreshSubscription();
      } else {
        setSubscription(null);
        setCredits(null);
      }
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        console.error("Session verification error:", err);
      }
      setUser(null);
      setSubscription(null);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const loginWithGoogle = useCallback(
    async (idToken: string, role?: "talent" | "company") => {
      const data = await api.post<{ user: User }>("/api/auth/google", { idToken, role });
      setUser(data.user);
      await refreshSubscription();
      return data.user;
    },
    [refreshSubscription]
  );

  const logout = useCallback(async () => {
    await api.post("/api/auth/logout");
    setUser(null);
    setSubscription(null);
    setCredits(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, subscription, credits, loginWithGoogle, logout, refresh, refreshSubscription, setUser }),
    [user, loading, subscription, credits, loginWithGoogle, logout, refresh, refreshSubscription]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
